import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { gbp } from "../../context/CartContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const emptyItem = { category_id: "", name: "", description: "", price: "", image_url: "", allergens: [], available: true, extras: [] };
const ALLERGENS = ["Gluten", "Milk", "Egg", "Fish", "Nuts", "Soya", "Sulphites", "Celery", "Mustard", "Sesame"];

export default function MenuManagement() {
  const [cats, setCats] = useState([]);
  const [menu, setMenu] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    const [c, m] = await Promise.all([api.get("/admin/categories"), api.get("/menu")]);
    setCats(c.data); setMenu(m.data);
  };
  useEffect(() => { load(); }, []);

  const addCategory = async () => {
    if (!newCat.trim()) return;
    await api.post("/admin/categories", { name: newCat, sort_order: cats.length, active: true });
    setNewCat(""); toast.success("Category added"); load();
  };
  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category and its items?")) return;
    await api.delete(`/admin/categories/${id}`); toast.success("Category deleted"); load();
  };

  const openNew = () => { setEditing({ ...emptyItem, category_id: cats[0]?.id || "" }); setDialogOpen(true); };
  const openEdit = (item) => { setEditing({ ...item, price: String(item.price) }); setDialogOpen(true); };

  const saveItem = async () => {
    if (!editing.name || !editing.category_id || editing.price === "") { toast.error("Name, category and price required"); return; }
    const payload = { ...editing, price: parseFloat(editing.price), extras: editing.extras.map((e) => ({ id: e.id || "", name: e.name, price: parseFloat(e.price) || 0 })) };
    try {
      if (editing.id) await api.patch(`/admin/menu-items/${editing.id}`, payload);
      else await api.post("/admin/menu-items", payload);
      toast.success("Saved"); setDialogOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/menu-items/${id}`); toast.success("Deleted"); load();
  };
  const toggleAvail = async (item) => {
    await api.patch(`/admin/menu-items/${item.id}/availability`, { available: !item.available });
    toast.success(item.available ? "Marked sold out" : "Marked available"); load();
  };

  const setE = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const toggleAllergen = (a) => setEditing((p) => ({ ...p, allergens: p.allergens.includes(a) ? p.allergens.filter((x) => x !== a) : [...p.allergens, a] }));
  const addExtra = () => setEditing((p) => ({ ...p, extras: [...p.extras, { name: "", price: 0 }] }));
  const setExtra = (i, k, v) => setEditing((p) => ({ ...p, extras: p.extras.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const removeExtra = (i) => setEditing((p) => ({ ...p, extras: p.extras.filter((_, idx) => idx !== i) }));

  return (
    <div className="px-4 md:px-6 py-4">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-head text-2xl md:text-3xl font-bold text-brand-dark">Menu Management</h1>
        <Button data-testid="add-item-btn" onClick={openNew} className="rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold">
          <Plus className="w-4 h-4 mr-1" /> Add item
        </Button>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-2xl p-4 border border-brand-dark/5 mb-6">
        <h2 className="font-head font-bold text-lg mb-3">Categories</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          {cats.map((c) => (
            <span key={c.id} className="flex items-center gap-2 bg-brand-cream px-3 py-1.5 rounded-full text-sm">
              {c.name}
              <button data-testid={`del-cat-${c.id}`} onClick={() => deleteCategory(c.id)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="rounded-xl h-10" />
          <Button data-testid="add-category-btn" onClick={addCategory} className="rounded-xl bg-brand-dark text-white">Add</Button>
        </div>
      </div>

      {/* Items grouped */}
      <div className="space-y-6">
        {menu.map((cat) => (
          <div key={cat.id}>
            <h2 className="font-head font-bold text-xl text-brand-dark mb-2">{cat.name}</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {cat.items.map((item) => (
                <div key={item.id} data-testid={`admin-item-${item.id}`} className="bg-white rounded-2xl p-3 border border-brand-dark/5 flex gap-3">
                  {item.image_url && <img src={item.image_url} alt="" className="w-16 h-16 rounded-xl object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-brand-dark truncate">{item.name}</p>
                      {!item.available && <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">Sold out</Badge>}
                    </div>
                    <p className="text-sm text-brand-teal font-bold">{gbp(item.price)}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <label className="flex items-center gap-1.5 text-xs text-brand-dark/60">
                        <Switch data-testid={`avail-${item.id}`} checked={item.available} onCheckedChange={() => toggleAvail(item)} /> Available
                      </label>
                      <button data-testid={`edit-item-${item.id}`} onClick={() => openEdit(item)} className="text-brand-dark/50 hover:text-brand-dark"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`del-item-${item.id}`} onClick={() => deleteItem(item.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">{editing?.id ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input data-testid="item-name" value={editing.name} onChange={(e) => setE("name", e.target.value)} className="rounded-xl mt-1" /></div>
              <div><Label className="text-xs">Category</Label>
                <select data-testid="item-category" value={editing.category_id} onChange={(e) => setE("category_id", e.target.value)} className="w-full rounded-xl border border-brand-dark/15 h-10 px-3 mt-1 bg-white">
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div><Label className="text-xs">Description</Label><Textarea value={editing.description} onChange={(e) => setE("description", e.target.value)} className="rounded-xl mt-1 resize-none" rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Price (£)</Label><Input data-testid="item-price" type="number" step="0.01" value={editing.price} onChange={(e) => setE("price", e.target.value)} className="rounded-xl mt-1" /></div>
                <div className="flex items-end"><label className="flex items-center gap-2 text-sm h-10"><Switch checked={editing.available} onCheckedChange={(v) => setE("available", v)} /> Available</label></div>
              </div>
              <div><Label className="text-xs">Photo URL</Label><Input value={editing.image_url} onChange={(e) => setE("image_url", e.target.value)} placeholder="https://…" className="rounded-xl mt-1" /></div>
              <div>
                <Label className="text-xs">Allergens</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {ALLERGENS.map((a) => (
                    <button key={a} type="button" onClick={() => toggleAllergen(a)}
                      className={`px-2.5 py-1 rounded-full text-xs border ${editing.allergens.includes(a) ? "bg-amber-100 border-amber-300 text-amber-800" : "border-brand-dark/15 text-brand-dark/50"}`}>{a}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Optional extras</Label>
                  <button data-testid="add-extra-btn" onClick={addExtra} className="text-brand-teal text-sm font-semibold flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
                </div>
                <div className="space-y-2 mt-1">
                  {editing.extras.map((e, i) => (
                    <div key={i} className="flex gap-2">
                      <Input value={e.name} onChange={(ev) => setExtra(i, "name", ev.target.value)} placeholder="e.g. Add cheese" className="rounded-xl h-9" />
                      <Input type="number" step="0.01" value={e.price} onChange={(ev) => setExtra(i, "price", ev.target.value)} placeholder="0.00" className="rounded-xl h-9 w-24" />
                      <button onClick={() => removeExtra(i)} className="text-red-400 shrink-0"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <Button data-testid="save-item-btn" onClick={saveItem} className="w-full h-12 rounded-xl bg-brand-teal hover:bg-brand-teal-dark text-white font-bold">Save item</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
